import firebaseApp from "../services/firebase";
import {useInfiniteQuery} from "react-query";
import {useParams} from "react-router-dom";
import {useEffect, useMemo, useRef, useState} from "react";
import {useRecoilValue} from "recoil";
import {
  firebaseClaimsState,
  firebaseOrganisationsState,
  firebaseSessionIdState,
  firebaseUidState,
  firebaseUserState
} from "../state/firebaseState";
import queryClient from "../services/queryClient";
import useUser from "./useUser";
import firebase from 'firebase/app';
import 'firebase/database';
import useTiming from "./useTiming";

const fetchChat = (msgRef, lastId) => {
  let ref = msgRef
  .orderByKey()
  .limitToLast(15)

  if(lastId) {
    ref = ref.endBefore(lastId);
  }

  return ref.once('value').then(snapshot => {
    const data = [];
    snapshot.forEach(item => {
      data.push({
        id: item.key,
        ...item.val(),
      });
    })
    return data;
  }).catch(() => {
    return [];
  })
};

const useLoadChat = () => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);
  const firebaseSessionId = useRecoilValue(firebaseSessionIdState);
  const firebaseClaims = useRecoilValue(firebaseClaimsState);
  const user = useRecoilValue(firebaseUserState);

  const msgRef = useMemo(() => {
    return firebaseSessionId ? firebaseApp.database().ref(`room-messages/${timing.id}`) : null;
  }, [timing.id, firebaseSessionId]);

  const count = queryClient.getQueryData([timing.id, 'chat'])?.pages?.[0]?.length || 0;

  const query = useInfiniteQuery(
    [timing.id, 'chat'],
    ({pageParam = null}) => fetchChat(msgRef, pageParam),
    {
      staleTime: Infinity,
      enabled: !!msgRef && count === 15,
      getNextPageParam: (lastGroup, allGroups) => {
        return lastGroup.find(i => true)?.id;
      },
      select: data => ({
        pages: [...data.pages].reverse(),
        pageParams: [...data.pageParams].reverse(),
      }),
    }
  );

  query.resetChat = () => {
    queryClient.setQueryData([timing.id, 'chat'], oldData => {
      return {
        pages: [
          (oldData?.pages?.[0] || [])
        ],
        pageParams: [undefined],
      };
    });
  };

  query.sendMessage = (message) => {
    if(!msgRef) {
      return;
    }

    const messageObj = {
      userId: firebaseUid,
      name: user.name,
      avatar: user?.avatar || null,
      timestamp: firebase.database.ServerValue.TIMESTAMP,
      message,
      isAdmin: firebaseClaims?.isAdmin || false,
      isOrganiser: firebaseClaims?.isOrganiser || false,
      type:  'default'
    }
    const newMessage = msgRef.push();

    return newMessage.setWithPriority(messageObj, firebase.database.ServerValue.TIMESTAMP);
  };

  useEffect(() => {
    if(!msgRef) {
      return;
    }

    let ref = msgRef
    .orderByKey()
    .limitToLast(15);

    const on = (cb) => (snapshot) => {
      queryClient.setQueryData([timing.id, 'chat'], oldData => {
        const data = oldData ? {...oldData} : {
          pages: [[]],
          pageParams: [undefined],
        };

        const itemIndex = data.pages[0].findIndex(i => i.id === snapshot.key);

        cb(data, {
          id: snapshot.key,
          ...snapshot.val(),
        }, itemIndex);

        return data;
      });
    }

    const onAdded = on((data, value, itemIndex) => {
      data.pages[0].push(value);
    });
    const onRemoved = on((data, value, itemIndex) => {
      if(itemIndex !== -1) {
        data.pages[0].splice(itemIndex, 1);
        data.pages[1] = data.pages[1] || [];
        data.pages[1].push(value);
      }
    });
    const onChanged = on((data, value, itemIndex) => {
      if(itemIndex !== -1) {
        data.pages[0][itemIndex] = value;
      }
    });

    ref.on('child_added', onAdded);
    ref.on('child_changed', onChanged);
    ref.on('child_removed', onRemoved);

    return () => {
      ref.off('child_added', onAdded);
      ref.off('child_changed', onChanged);
      ref.off('child_removed', onRemoved);

      queryClient.removeQueries([timing.id, 'chat']);
    }
  }, [msgRef]);

  return query;
}

export default useLoadChat;
